import type React from "react";
import { useEffect, useState } from "react";
import { Link, useLoaderData } from "react-router-dom";

import mapImage from "../../resources/map.jpg";
import { FeedbackModal } from "../components/FeedbackModal";
import { FacebookIcon, InstagramIcon, SocialLink, YoutubeIcon } from "../components/icons";
import { LinkifiedDescription } from "../components/LinkifiedDescriptions";
import {
  getAllStages,
  getEventMeta,
  loadFestivalData,
  getStageColor,
} from "../services/dataService";
import type { FestivalInfo, Stage } from "../types";
import { BasePage } from "./BasePage";

export const loadFestivalInfo = async () => {
  await loadFestivalData();
  return { eventInfo: getEventMeta(), stages: getAllStages() };
};

function InfoPageHeroSection({ eventInfo }: { eventInfo: FestivalInfo }) {
  return (
    <div className="bg-[#222222]/65 border border-white/5 rounded-2xl p-6 relative overflow-hidden backdrop-blur-md">
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#8667B9]/10 to-[#22D3BA]/10 blur-xl pointer-events-none" />
      <h1 className="font-['Unbounded'] text-3xl font-extrabold uppercase tracking-wide text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.15)]">
        {eventInfo.eventName}
      </h1>
      <p className="font-['Unbounded'] text-lg font-semibold mt-2.5 text-main uppercase tracking-wide">
        ,,{eventInfo.tagline}“
      </p>
      <div className="mt-4 space-y-1.5 text-sm text-muted">
        <p className="flex items-center gap-2">
          <span>📆</span> <strong className="text-white">{eventInfo.dates}</strong>
        </p>
        <p className="flex items-center gap-2">
          <span>📍</span> <strong className="text-white">{eventInfo.location}</strong>
        </p>
      </div>
      <div className="mt-6 flex flex-col gap-2.5">
        <a
          aria-label={eventInfo.links.maraton.name}
          href={eventInfo.links.maraton.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full text-center bg-white/5 border border-white/10 text-white py-2.5 rounded-xl font-semibold hover:bg-white/10 hover:border-main/40 transition-all text-sm"
        >
          {eventInfo.links.maraton.name}
        </a>
        {eventInfo.links.radio && (
          <a
            aria-label={eventInfo.links.radio.name}
            href={eventInfo.links.radio.url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-center bg-white/5 border border-white/10 text-white py-2.5 rounded-xl font-semibold hover:bg-white/10 hover:border-main/40 transition-all text-sm"
          >
            {eventInfo.links.radio.name}
          </a>
        )}
        <a
          aria-label={eventInfo.links.facebook.name}
          href={eventInfo.links.facebook.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full text-center bg-white/5 border border-white/10 text-white py-2.5 rounded-xl font-semibold hover:bg-white/10 hover:border-pink/40 transition-all flex items-center justify-center gap-2 text-sm"
        >
          <FacebookIcon className="w-4 h-4 fill-current text-white" />
          {eventInfo.links.facebook.name}
        </a>
        <div className="grid grid-cols-2 gap-2.5">
          <a
            aria-label={eventInfo.links.tickets.name}
            href={eventInfo.links.tickets.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center bg-pink/15 border border-pink/35 text-pink py-2.5 rounded-xl font-semibold hover:bg-pink/25 transition-all text-xs"
          >
            {eventInfo.links.tickets.name}
          </a>
          <a
            aria-label={eventInfo.links.program.name}
            href={eventInfo.links.program.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center bg-accent/15 border border-accent/35 text-accent py-2.5 rounded-xl font-semibold hover:bg-accent/25 transition-all text-xs"
          >
            {eventInfo.links.program.name}
          </a>
        </div>
        <a
          aria-label="Térkép megnyitása"
          href={mapImage}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-orange/15 border border-orange/35 text-orange py-2.5 rounded-xl font-semibold hover:bg-orange/25 transition-all text-sm"
        >
          <span>🗺️</span> Térkép megnyitása
        </a>
      </div>
    </div>
  );
}

// Custom wrapper card to prevent using BasePage Card directly
function InfoCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`bg-[#222222]/65 border border-white/5 rounded-2xl p-5 backdrop-blur-md ${className}`}
    >
      {children}
    </div>
  );
}

function InfoPageAboutSection({ eventInfo }: { eventInfo: FestivalInfo }) {
  return (
    <InfoCard>
      <h2 className="font-['Unbounded'] text-lg font-bold text-main mb-3 uppercase tracking-wider">
        Mi az a Tilos Maraton?
      </h2>
      <div className="space-y-3.5 text-sm text-muted leading-relaxed">
        {eventInfo.description.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: description is a static array
          <LinkifiedDescription key={index} description={item} className="" />
        ))}
      </div>
    </InfoCard>
  );
}

function InfoPageDirectivesSection({ eventInfo }: { eventInfo: FestivalInfo }) {
  return (
    <InfoCard>
      <h2 className="font-['Unbounded'] text-lg font-bold text-pink mb-3 uppercase tracking-wider">
        Fontos!
      </h2>
      <div className="space-y-3.5 text-sm text-muted leading-relaxed">
        {eventInfo.generalDirectives.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: directives is a static array
          <LinkifiedDescription key={index} description={item} className="" />
        ))}
      </div>
    </InfoCard>
  );
}

function InfoPageLocationSection({ eventInfo }: { eventInfo: FestivalInfo }) {
  return (
    <InfoCard>
      <h2 className="font-['Unbounded'] text-lg font-bold text-orange mb-3 uppercase tracking-wider">
        Helyszín
      </h2>
      <div className="space-y-3">
        <a
          href={eventInfo.links.googleMaps.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block group hover:no-underline"
        >
          <div className="p-3 bg-white/5 border border-white/5 rounded-xl transition-all group-hover:border-orange/40">
            <p className="text-sm font-semibold text-white group-hover:text-orange transition-colors">
              {eventInfo.location}
            </p>
            <p className="text-xs text-muted mt-1">{eventInfo.address}</p>
          </div>
        </a>
      </div>
    </InfoCard>
  );
}

function InfoPageStagesSection({ stages }: { stages: Stage[] }) {
  return (
    <InfoCard>
      <h2 className="font-['Unbounded'] text-lg font-bold text-accent mb-4 uppercase tracking-wider">
        Színpadok
      </h2>
      <div className="space-y-3">
        {stages
          .filter((e) => !e.hidden)
          .map((stage) => {
            const stageColor = getStageColor(stage.id);
            return (
              <div
                key={stage.id}
                className="relative flex items-stretch border border-white/5 bg-white/[0.02] rounded-xl overflow-hidden hover:bg-white/[0.04] transition-all"
              >
                <div className="w-1 flex-shrink-0" style={{ backgroundColor: stageColor }} />
                <Link
                  to={`/stage/${stage.id}`}
                  className="block flex-1 p-3.5 group hover:no-underline"
                  aria-label={stage.name}
                >
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center">
                      <span
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: stageColor }}
                      />
                      <h3 className="font-['Unbounded'] font-bold text-sm text-white group-hover:text-main transition-colors uppercase tracking-wide">
                        {stage.name}
                      </h3>
                    </div>
                    <p className="text-xs text-muted leading-relaxed mt-1">{stage.description}</p>
                  </div>
                </Link>
              </div>
            );
          })}
      </div>
    </InfoCard>
  );
}

function InfoPageContactSection({ eventInfo }: { eventInfo: FestivalInfo }) {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  return (
    <>
      <InfoCard>
        <h2 className="font-['Unbounded'] text-lg font-bold text-white mb-3.5 uppercase tracking-wider">
          Kapcsolat
        </h2>
        <div className="space-y-2.5 text-xs text-muted">
          <div className="flex justify-between py-1.5 border-b border-white/5">
            <span>Email</span>
            <a
              href={`mailto:${eventInfo.contact}`}
              className="font-semibold text-white hover:text-main"
            >
              {eventInfo.contact}
            </a>
          </div>
          {eventInfo.contacts.map((contact) => (
            <div key={contact.role} className="flex justify-between py-1.5 border-b border-white/5">
              <span>{contact.role}</span>
              {contact.email ? (
                <a
                  href={`mailto:${contact.email}`}
                  className="font-semibold text-white hover:text-main"
                >
                  {contact.email}
                </a>
              ) : (
                <span className="font-semibold text-white">{contact.name}</span>
              )}
            </div>
          ))}
          <div className="flex justify-between py-1.5 border-b border-white/5">
            <span>Adástelefon</span>
            <a href="tel:+3612153773" className="font-semibold text-white hover:text-main">
              +36 1 215 3773
            </a>
          </div>

          <div className="pt-4 border-t border-white/5 flex flex-col gap-2">
            <p className="text-muted leading-relaxed">
              Visszajelzés az alkalmazással, a maratonnal, vagy a rádió működésével kapcsolatban
            </p>
            <button
              type="button"
              onClick={() => setIsFeedbackOpen(true)}
              className="w-full text-center bg-main/10 border border-main/35 text-main py-2 px-4 rounded-xl font-semibold hover:bg-main/20 hover:border-main/50 transition-all text-xs cursor-pointer"
              aria-label="Visszajelzés küldő űrlap megnyitása"
            >
              Visszajelzés küldése
            </button>
          </div>

          <div className="pt-4 mt-2">
            <h3 className="font-bold text-white mb-3 text-sm uppercase tracking-wide">
              Kövess minket!
            </h3>
            <div className="flex space-x-3">
              <SocialLink
                href={eventInfo.links.facebook.url}
                icon={<FacebookIcon className="w-5 h-5 fill-current" />}
                label="Facebook"
              />
              <SocialLink
                href={eventInfo.links.instagram.url}
                icon={<InstagramIcon className="w-5 h-5 fill-current" />}
                label="Instagram"
              />
              <SocialLink
                href={eventInfo.links.youtube.url}
                icon={<YoutubeIcon className="w-5 h-5 fill-current" />}
                label="YouTube"
              />
            </div>
          </div>
        </div>
      </InfoCard>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  );
}

function PwaInstallCard({ eventInfo }: { eventInfo: FestivalInfo }) {
  const [isInstallable, setIsInstallable] = useState(!!window.deferredPrompt);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (navigator as any).standalone === true ||
        document.referrer.includes("android-app://");
      setIsInstalled(isStandalone);
    };

    checkStandalone();

    // Check if iOS (iPhone/iPad/iPod)
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/i.test(ua);
    setIsIOS(isIOSDevice && isSafari);

    // Listen for custom installable event
    const handleInstallable = () => {
      setIsInstallable(true);
    };

    window.addEventListener("pwa-installable", handleInstallable);

    // Also check periodically in case prompt is captured before mount
    const timer = setInterval(() => {
      if (window.deferredPrompt) {
        setIsInstallable(true);
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      window.removeEventListener("pwa-installable", handleInstallable);
      clearInterval(timer);
    };
  }, []);

  const handleInstallClick = async () => {
    const promptEvent = window.deferredPrompt;
    if (!promptEvent) return;

    // Show the native browser install prompt
    promptEvent.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await promptEvent.userChoice;
    console.debug(`[PWA Install] User outcome: ${outcome}`);

    // Clear the deferred prompt variable since it can only be used once
    window.deferredPrompt = null;
    setIsInstallable(false);
  };

  // If already installed, show a clean, small badge of honor
  if (isInstalled) {
    return (
      <InfoCard className="border-main/25 bg-main/5">
        <div className="flex items-center gap-3">
          <span className="text-xl">📱</span>
          <div>
            <h3 className="font-['Unbounded'] font-bold text-xs text-white uppercase tracking-wider">
              {eventInfo.eventName}
            </h3>
            <p className="text-xs text-muted mt-0.5">
              Alkalmazásként futtatva. Köszönjük, hogy telepítetted!
            </p>
          </div>
        </div>
      </InfoCard>
    );
  }

  // If installable (Android/Chrome/Edge/Windows/macOS), show a beautiful pulsing action button
  if (isInstallable) {
    return (
      <InfoCard className="relative overflow-hidden border-orange/25 shadow-[0_0_15px_rgba(241,119,62,0.15)] bg-gradient-to-r from-[#222222]/80 to-background/80">
        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange/10 to-pink/10 blur-xl pointer-events-none" />
        <h2 className="font-['Unbounded'] text-sm font-bold text-orange mb-2.5 uppercase tracking-wider flex items-center gap-1.5">
          <span>📲</span> Programfüzet a telódra!
        </h2>
        <p className="text-xs text-muted leading-relaxed mb-4">
          Telepítsd a Tilos Maraton műsorfüzetet a kezdőképernyődre, hogy offline is azonnal elérd a
          színpadok és fellépők programjait, gyorsabban töltsön be, és teljes képernyőn fusson!
        </p>
        <button
          type="button"
          onClick={handleInstallClick}
          className="w-full text-center bg-gradient-to-r from-orange to-pink text-white py-2.5 rounded-xl font-['Unbounded'] text-xs font-bold hover:brightness-110 shadow-lg shadow-orange/20 transition-all uppercase tracking-wider active:scale-[0.98] animate-pulse"
          aria-label="Alkalmazás telepítése a kezdőképernyőre"
        >
          Alkalmazás Telepítése
        </button>
      </InfoCard>
    );
  }

  // If on iOS (Safari), show a beautiful helpful guide
  if (isIOS) {
    return (
      <InfoCard className="border-accent/25 bg-gradient-to-r from-[#222222]/80 to-background/80">
        <h2 className="font-['Unbounded'] text-sm font-bold text-accent mb-2.5 uppercase tracking-wider flex items-center gap-1.5">
          <span>🍎</span> Telepítés iOS eszközre
        </h2>
        <p className="text-xs text-muted leading-relaxed mb-3">
          Telepítsd a Tilos Maraton műsorfüzetet a kezdőképernyődre, hogy offline is azonnal elérd a
          színpadok és fellépők programjait, gyorsabban töltsön be, és teljes képernyőn fusson!
        </p>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-xs text-muted space-y-2">
          <p className="flex items-start gap-2">
            <span className="text-accent font-bold">1.</span>
            <span>
              Koppints a Safari böngésző alján lévő <strong>Megosztás</strong> gombra (
              <span className="text-lg leading-none inline-block">⎋</span> /{" "}
              <span className="text-base leading-none">📤</span>).
            </span>
          </p>
          <p className="flex items-start gap-2">
            <span className="text-accent font-bold">2.</span>
            <span>
              Görgess le és válaszd a <strong>Főképernyőhöz adás</strong> (
              <span className="text-lg leading-none inline-block">⊕</span>) opciót.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <span className="text-accent font-bold">3.</span>
            <span>
              Koppints a jobb felső sarokban lévő <strong>Hozzáadás</strong> gombra.
            </span>
          </p>
        </div>
      </InfoCard>
    );
  }

  // Fallback / General PWA installation info for other platforms
  return (
    <InfoCard className="border-main/10">
      <h2 className="font-['Unbounded'] text-sm font-bold text-main mb-2.5 uppercase tracking-wider flex items-center gap-1.5">
        <span>📱</span> PWA Alkalmazás
      </h2>
      <p className="text-xs text-muted leading-relaxed">
        Ez a weboldal egy Progressive Web App (PWA). Böngésződ beállításaiban vagy a címsor végén
        lévő ikonra kattintva telepítheted mobilra vagy asztali gépre, így offline is teljesen
        működőképes marad!
      </p>
    </InfoCard>
  );
}

function InfoPageResetSection() {
  const [resetState, setResetState] = useState<"idle" | "confirm" | "done">("idle");

  const handleReset = () => {
    if (resetState === "idle") {
      setResetState("confirm");
      return;
    }

    // Clear localStorage keys
    localStorage.clear();
    setResetState("done");

    // Auto-reload the site after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  return (
    <InfoCard className="border-red-500/10 flex flex-col items-center text-center">
      <h2 className="font-['Unbounded'] text-xs font-bold text-red-500 mb-2 uppercase tracking-wider">
        Beállítások visszaállítása
      </h2>
      <p className="text-xs text-muted leading-relaxed mb-4">
        Ezzel törölheted a kedvenceidet, a beállított szűrőket és a sütikre vonatkozó döntésedet is.
      </p>
      <button
        type="button"
        onClick={handleReset}
        disabled={resetState === "done"}
        className={`w-full text-center py-2.5 rounded-xl font-['Unbounded'] text-xs font-bold transition-all uppercase tracking-wider active:scale-[0.98] cursor-pointer border ${
          resetState === "idle"
            ? "bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
            : resetState === "confirm"
              ? "bg-red-600 text-white border-red-700 hover:bg-red-700 shadow-lg shadow-red-600/20 animate-pulse"
              : "bg-green-600 text-white border-green-700"
        }`}
        aria-label={
          resetState === "idle"
            ? "Mentett adatok és beállítások törlése"
            : resetState === "confirm"
              ? "Biztosan törlöd a mentett adatokat és beállításokat? Kattints a megerősítéshez."
              : "Mentett adatok sikeresen törölve"
        }
      >
        {resetState === "idle" && "Mentett adatok/beállítások törlése"}
        {resetState === "confirm" && "Biztosan törlöd? Kattints újra!"}
        {resetState === "done" && "Sikeresen törölve! Újratöltés..."}
      </button>
      {resetState === "confirm" && (
        <button
          type="button"
          onClick={() => setResetState("idle")}
          className="text-xs text-muted underline mt-3 hover:text-white cursor-pointer"
          aria-label="Visszaállítás elvetése"
        >
          Mégsem
        </button>
      )}
    </InfoCard>
  );
}

export const InfoPage: React.FC = () => {
  const { eventInfo, stages } = useLoaderData<typeof loadFestivalInfo>();

  return (
    <BasePage>
      <div className="space-y-6 pb-6">
        <InfoPageHeroSection eventInfo={eventInfo} />
        <InfoPageAboutSection eventInfo={eventInfo} />
        <InfoPageDirectivesSection eventInfo={eventInfo} />
        <InfoPageLocationSection eventInfo={eventInfo} />
        <InfoPageStagesSection stages={stages} />
        <PwaInstallCard eventInfo={eventInfo} />
        <InfoPageContactSection eventInfo={eventInfo} />
        <InfoPageResetSection />
      </div>
    </BasePage>
  );
};

export default InfoPage;
